import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import styles from './PrivacyPolicyPage.module.css'

function Section({ title, children }) {
  return (
    <div className={styles.section}>
      <h2 className={styles.sectionTitle}>
        <span className={styles.sectionBar} aria-hidden="true" />
        {title}
      </h2>
      <div className={styles.sectionBody}>{children}</div>
    </div>
  )
}

function Brand({ children }) {
  return <strong className={styles.brand}>{children}</strong>
}

function Highlight({ children }) {
  return <span className={styles.highlight}>{children}</span>
}

export default function PrivacyPolicyPage() {
  useEffect(() => { window.scrollTo(0, 0) }, [])

  return (
    <div className={styles.page}>
      {/* ── Page header ── */}
      <div className={styles.header}>
        <div className={styles.headerInner}>
          <Link to="/" className={styles.back}>
            <span className={styles.backArrow}>←</span> Back to Home
          </Link>
          <div className={styles.headerTitle}>
            <div className={styles.iconWrap}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </div>
            <div>
              <h1 className={styles.h1}>Privacy Policy</h1>
              <p className={styles.subtitle}>NexusAIWeb &amp; TechIntelliVerse Private Limited · Last updated: May 2025</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <div className={styles.body}>
        <div className={styles.card}>

          {/* Brand strip */}
          <div className={styles.pillStrip}>
            {[
              { name: 'NexusAIWeb',  cls: styles.pillOrange },
              { name: 'PromptNexus', cls: styles.pillIndigo },
              { name: 'InternNexus', cls: styles.pillCyan },
              { name: 'SkillNexus',  cls: styles.pillIndigo },
              { name: 'MentorNexus', cls: styles.pillGold },
            ].map(b => (
              <span key={b.name} className={`${styles.pill} ${b.cls}`}>{b.name}</span>
            ))}
            <span className={`${styles.pill} ${styles.pillMuted}`}>
              Brand names of TechIntelliVerse Pvt. Ltd.
            </span>
          </div>

          <p className={styles.intro}>
            This Privacy Policy is published by{' '}
            <Brand>TechIntelliVerse Private Limited</Brand> ("TechIntelliVerse", "we", "us", or "our"), the
            legal entity that owns and operates all NexusAIWeb ecosystem platforms. The brand names{' '}
            <Brand>NexusAIWeb</Brand>, <Brand>PromptNexus</Brand>, <Brand>InternNexus</Brand>,{' '}
            <Brand>SkillNexus</Brand>, and <Brand>MentorNexus</Brand> are registered trademarks and brand
            names of TechIntelliVerse Private Limited.
          </p>
          <p className={styles.intro}>
            This policy applies to your use of the <Brand>NexusAIWeb</Brand> platform, accessible
            at <Highlight>nexusaiweb.com</Highlight> and all related subdomains (pn.nexusaiweb.com, etc.). Your
            privacy is critically important to us, and we are committed to protecting it.
          </p>

          <Section title="Information We Collect Automatically">
            <p>When you visit NexusAIWeb or any sub-platform, we automatically collect certain non-personal information, including:</p>
            <ul>
              <li>Browser type and version</li>
              <li>Operating system</li>
              <li>Referring URLs and pages visited</li>
              <li>Date and time of access</li>
              <li>IP address (anonymised for analytics)</li>
              <li>Device type (desktop, mobile, tablet)</li>
            </ul>
            <p>This information is used solely to analyse trends, administer the platforms, and gather demographic information for aggregate use. It is not linked to any personally identifiable information.</p>
          </Section>

          <Section title="Personal Information You Provide">
            <p>We collect personal information only when you voluntarily provide it, such as when you:</p>
            <ul>
              <li>Register for a NexusAIWeb account (name, email, phone number)</li>
              <li>Complete your profile (institution, year of study)</li>
              <li>Register for PromptNexus challenges or PromptPath programs</li>
              <li>Submit solutions in the PromptNexus arena</li>
              <li>Contact us via the contact form or support email</li>
              <li>Sign up for early access to InternNexus, SkillNexus, or MentorNexus</li>
            </ul>
            <p>This information is used to provide and improve our services, issue certificates, notify you of results, and communicate important platform updates across the NexusAIWeb ecosystem.</p>
          </Section>

          <Section title="Cookies">
            <p>NexusAIWeb web platforms use cookies to enhance your experience. Cookies are small files stored on your device that help us remember your preferences and session state.</p>
            <p>We use the following types of cookies:</p>
            <ul>
              <li><strong>Essential cookies:</strong> Required for authentication, session management, and core platform functionality.</li>
              <li><strong>Analytics cookies:</strong> Used via Google Analytics (GA4) to understand how visitors interact with our platforms. Data is anonymised and aggregated.</li>
              <li><strong>Preference cookies:</strong> Store your theme preference (dark/light mode) and UI settings.</li>
            </ul>
            <p>You may disable cookies in your browser settings; however, some platform features may not function correctly without them.</p>
          </Section>

          <Section title="How We Use Your Information">
            <ul>
              <li>To create and manage your NexusAIWeb ecosystem account (used across PromptNexus, InternNexus, SkillNexus, MentorNexus)</li>
              <li>To deliver PromptNexus challenge results, leaderboards, and AI-evaluated certificates</li>
              <li>To process payments for paid PromptPath programs or challenge registrations (via Razorpay)</li>
              <li>To send transactional emails: OTP verification, result notifications, certificate issuance</li>
              <li>To send platform announcements and NexusAIWeb ecosystem updates (you may unsubscribe at any time)</li>
              <li>To improve our platforms based on aggregate usage analytics</li>
              <li>To comply with legal obligations</li>
            </ul>
          </Section>

          <Section title="Email Communications">
            <p>
              By registering on any NexusAIWeb platform, you consent to receive transactional emails related to
              your account (OTP, challenge results, certificates). You may also receive periodic communications
              about new challenges, PromptPath programs, and NexusAIWeb ecosystem updates.
            </p>
            <p>
              All marketing emails include an unsubscribe link. You may opt out at any time. Transactional
              emails (OTP, certificate delivery) are essential to service delivery and cannot be unsubscribed from.
            </p>
          </Section>

          <Section title="Third-Party Services">
            <p>We integrate with the following third-party services, each governed by their own privacy policy:</p>
            <ul>
              <li><strong>Google Analytics (GA4)</strong> — for anonymous, aggregated usage analytics</li>
              <li><strong>Razorpay</strong> — for secure payment processing (we do not store card or banking details on our servers)</li>
              <li><strong>LinkedIn OAuth</strong> — for social sign-in (we only receive your name, email, and profile photo)</li>
              <li><strong>Google OAuth</strong> — for social sign-in</li>
              <li><strong>Azure AI Services</strong> — for evaluating PromptNexus challenge submissions via NexusEval (processed for evaluation only, not retained by Azure)</li>
            </ul>
            <p>Our platforms may contain links to third-party websites. TechIntelliVerse is not responsible for the privacy practices of those sites.</p>
          </Section>

          <Section title="Payment, Cancellation & Refund Policy">
            <p>Payments are processed securely via <strong>Razorpay</strong>. TechIntelliVerse Private Limited does not store your card or banking details.</p>
            <p><strong>Challenge Registration Fees:</strong> Once a paid PromptNexus challenge registration is confirmed, fees are non-refundable. If a challenge is cancelled by TechIntelliVerse, a full refund will be issued within 7 business days.</p>
            <p><strong>PromptPath Programs:</strong> You may request a full refund within 24 hours of purchase, provided you have not accessed more than 10% of the program content.</p>
            <p>To request a refund, email <Highlight>support@nexusaiweb.com</Highlight> with your order ID and reason. Refunds are typically processed within 5–7 business days.</p>
          </Section>

          <Section title="Data Security">
            <p>TechIntelliVerse implements industry-standard security measures to protect your personal information, including:</p>
            <ul>
              <li>HTTPS/TLS encryption for all data in transit</li>
              <li>Bcrypt password hashing (passwords are never stored in plaintext)</li>
              <li>JWT-based authentication with Redis token whitelisting</li>
              <li>Access controls limiting data access to authorised personnel only</li>
            </ul>
            <p>No method of transmission over the internet is 100% secure. While we strive to protect your data, we cannot guarantee absolute security.</p>
          </Section>

          <Section title="Data Retention">
            <p>
              We retain your personal information for as long as your account is active or as needed to provide services.
              You may request deletion of your account and associated data by contacting{' '}
              <Highlight>support@nexusaiweb.com</Highlight>. Certain data (e.g., issued certificates, payment
              transaction records) may be retained for legal and audit purposes even after account deletion.
            </p>
          </Section>

          <Section title="Children's Privacy">
            <p>
              NexusAIWeb platforms are not directed at children under 13 years of age. TechIntelliVerse does not
              knowingly collect personal information from children under 13. If you believe we have inadvertently
              collected such information, please contact us immediately at{' '}
              <Highlight>support@nexusaiweb.com</Highlight>.
            </p>
          </Section>

          <Section title="Your Rights">
            <p>You have the right to:</p>
            <ul>
              <li>Access the personal data we hold about you</li>
              <li>Request correction of inaccurate or incomplete data</li>
              <li>Request deletion of your data (subject to legal retention obligations)</li>
              <li>Opt out of marketing and promotional communications</li>
              <li>Lodge a complaint with a data protection authority</li>
            </ul>
            <p>To exercise any of these rights, email us at <Highlight>privacy@nexusaiweb.com</Highlight>.</p>
          </Section>

          <Section title="Changes to This Policy">
            <p>
              TechIntelliVerse may update this Privacy Policy from time to time. We will notify you of significant
              changes by posting the revised policy on this page with an updated effective date and, where
              appropriate, by email notification.
            </p>
            <p>Your continued use of any NexusAIWeb platform after changes are posted constitutes acceptance of the updated policy.</p>
          </Section>

          <Section title="Contact Us">
            <p>For any questions, concerns, or data-related requests regarding this Privacy Policy, please contact:</p>
            <div className={styles.contactBox}>
              <p className={styles.contactName}>TechIntelliVerse Private Limited</p>
              <p className={styles.contactLine}>Brands: NexusAIWeb · PromptNexus · InternNexus · SkillNexus · MentorNexus</p>
              <p className={styles.contactLine}>Privacy: privacy@nexusaiweb.com</p>
              <p className={styles.contactLine}>Support: support@nexusaiweb.com</p>
              <p className={styles.contactLine}>Website: nexusaiweb.com</p>
            </div>
          </Section>

        </div>

        {/* Page footer strip */}
        <div className={styles.pageFooter}>
          <span className={styles.pageFooterBrand}>NexusAIWeb</span>
          <span className={styles.pageFooterDot}>·</span>
          <span className={styles.pageFooterText}>TechIntelliVerse Private Limited</span>
          <span className={styles.pageFooterDot}>·</span>
          <Link to="/terms" className={styles.pageFooterLink}>Terms &amp; Conditions</Link>
          <span className={styles.pageFooterDot}>·</span>
          <Link to="/" className={styles.pageFooterLink}>Home</Link>
        </div>
      </div>
    </div>
  )
}
